import { CommonModule } from '@angular/common';
import { Component, Injector, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { openMergeWizard } from '@app/components/clients/merge-wizard/shared/open-merge-wizard';
import { ClientPermissionsCheckerService } from '@app/services/client-permissions-checker.service';
import ActionsCtaComponent from '@app/shared/common/actions-cta/actions-cta.component';
import AddDocumentPopupComponent from '@app/shared/common/add-document-popup/add-document-popup.component';
import AppGuidelinesModule from '@app/shared/common/app-guidelines.module';
import { IMenuItem } from '@app/shared/common/match/match-submenu/type';
import { ConfirmDialogComponent, IConfirmDialogData } from '@app/shared/confirm-dialog/confirm-dialog.component';
import SvgIconComponent from '@app/shared/svg-icon/svg-icon.component';
import { filter, firstValueFrom, switchMap } from '@node_modules/rxjs';
import { ClientTypeNames } from '@shared/AppConsts';
import AppPermissions from '@shared/AppPermissionNames';
import AppComponentBase from '@shared/common/app-component-base';
import { ClientActionType } from '@shared/common/common-types';
import { EmailPipe } from '@shared/common/pipes/email.pipe';
import FormatDatePipe from '@shared/common/pipes/format-date.pipe';
import { PhoneNumberPipe } from '@shared/common/pipes/phone-number.pipe';
import { ApproveExternalBrokerRequestModel,
    ClientDetailsResponseModel,
    ClientReferralRequestModel,
    ClientsReferralsServiceProxy,
    ExternalBrokerApprovalStatus,
    RequestExternalBrokerRequestModel } from '@shared/service-proxies/service-proxies';
import ClientsService from '@shared/utils/clients.service';
import UtilsModule from '@shared/utils/utils.module';
import { groupBy, isEmpty, isNil,map } from 'lodash';
import { DateTime } from 'luxon';

@Component({
    selector: 'app-client-card',
    imports: [
        CommonModule,
        AppGuidelinesModule,
        ActionsCtaComponent,
        UtilsModule,
        PhoneNumberPipe,
        EmailPipe,
        FormatDatePipe,
        SvgIconComponent,
        RouterLink,
    ],
    templateUrl: './client-card.component.html',
    styleUrls: ['./client-card.component.less'],
})
class ClientCardComponent extends AppComponentBase implements OnInit {
    @Input() clientId?: number;

    @Input() clientExternalSystemId?: number;

    @Input() clientName?: string;

    @Input() clientTypeName?: string;

    @Input() clientPhones?: string[] = [];

    @Input() clientEmails?: string;

    @Input() clientWorkplace?: string;

    @Input() clientPosition?: string;

    @Input() clientBirthDate?: DateTime;

    @Input() clientSource?: string;

    @Input() isExpandable = true;

    @Input() isExpanded = false;

    @Input() shouldRedirectToClientDetails = false;

    @Input() clientUnifiedIdentificationCode?: string;

    @Input() client?: ClientDetailsResponseModel;

    @Input() canViewData = true;

    @Input() approvalStatus?: ExternalBrokerApprovalStatus;

    @Input() referralId?: number;

    @Input() userId?: number;

    @Input() certificate?: number;

    menuItems: IMenuItem[] = [];

    constructor(
        injector: Injector,
        private dialog: MatDialog,
        private router: Router,
        private contactPermissionChecker: ClientPermissionsCheckerService,
        private clientsReferralService: ClientsReferralsServiceProxy,
        private clientService: ClientsService,
    ) {
        super(injector);
    }

    get clientTitle(): string {
        return this.clientId
            ? `${this.clientId}`
            : this.l('Client');
    }

    get clientSectors(): string {
        if (!this.client?.clientsSectors || this.client.clientsSectors.length === 0) {
            return '';
        }

        return this.client.clientsSectors
            .map(sector => sector.sectorName)
            .join(', ');
    }

    get clientTagsByCategory() {
        const tags = this.client?.clientsTags;

        if (isEmpty(tags)) {
            return [];
        }

        return map(
            groupBy(tags, t => t.tagCategoryName || ''),
            (items, category) => ({
                category,
                tags: items
                    .map(t => t.tagName)
                    .filter(Boolean),
            })
        );
    }

    get clientBusinessOrganizations(): string {
        const items = this.client?.clientBusinessOrganizations;

        if (isEmpty(items)) {
            return '';
        }

        return items
            .map(x => x.clientBusinessOrganizationTypeName)
            .filter(Boolean)
            .join(', ');
    }

    get clientSegments(): string {
        const segments = this.client?.clientSegments;

        if (isEmpty(segments)) {
            return '';
        }

        return segments
            .map(x => x.clientSegmentTypeName)
            .filter(Boolean)
            .join(', ');
    }

    get clientPreferences(): string {
        const preferences = this.client?.clientsPreferences;

        if (isEmpty(preferences)) {
            return '';
        }

        return preferences
            .map((x) => x.clientPreferenceName)
            .filter(Boolean)
            .join(', ');
    }

    get clientEmbassy(): string {
        if (!this.client?.isEmbassy) {
            return '';
        }
        return this.client.embassyCountryName || '';
    }

    get hasCertificate(): boolean {
        return !isNil(this.certificate);
    }

    ngOnInit() {
        if (!isNil(this.client)) {
            this.addMenuItems(this.client);
        }
    }

    toggleExpand(event: MouseEvent) {
        event.preventDefault();

        this.isExpanded = !this.isExpanded;
    }

    redirectToClientDetails() {
        if (this.shouldRedirectToClientDetails) {
            window.open('/app/clients/details/' + this.clientId, '_blank');
        }
    }

    async handleActionClicked(actionType: ClientActionType) {
        const identifier = this.clientUnifiedIdentificationCode;
        switch (actionType) {
            case ClientActionType.AddNewDocument:
                this.dialog.open(AddDocumentPopupComponent, { data: { clientId: this.clientId, disableClose: true } });
                break;
            case ClientActionType.CreateSearch: {
                this.router.navigate(['/app/searches/add'], { queryParams: { clientId: this.clientId } });
                break;
            }
            case ClientActionType.CreateOffer: {
                this.router.navigate(['/app/offers/add'], { queryParams: { clientId: this.clientId } });
                break;
            }
            case ClientActionType.Edit:
                this.router.navigate([`/app/clients/edit/${this.clientId}`]);
                break;
            case ClientActionType.Project:
                this.router.navigate(['/app/projects/add-from-client'], { queryParams: { identifier } });
                break;
            case ClientActionType.Merge:
                // The wizard opens in a NEW window (danny, 2026-07-31); everything
                // inside it, restart included, stays in-window.
                openMergeWizard(this.router, this.clientId);
                break;
            case ClientActionType.AddExternalBroker: {
                const confirmed = await this.clientService.confirmAction(
                    this.l('SubmitRequest'),
                    this.l('ConfirmBrokerRequest')
                );
                // The approvers are notified only once the request is persisted, otherwise they get
                // a notification for a request that never reached the Requested state.
                const notifyApprovers = () => {
                    this.clientsReferralService.sendExternalBrokerRequestNotifications({
                        clientId: this.clientId,
                        clientName: this.clientName,
                    } as RequestExternalBrokerRequestModel)
                        .subscribe();
                };

                if (confirmed && isNil(this.referralId)) {
                    this.createExternalBrokerRequest(notifyApprovers);
                } else if (
                    confirmed &&
                    !isNil(this.referralId) &&
                    (this.approvalStatus === ExternalBrokerApprovalStatus.Declined ||
                    isNil(this.approvalStatus))) {
                    this.updateExternalBrokerRequest(
                        this.referralId,
                        ExternalBrokerApprovalStatus.Requested,
                        null,
                        notifyApprovers,
                    );
                }

                break;
            }
            case ClientActionType.ApproveExternalBroker: {
                const firstCertificateNumber = 1;
                const certificateNumberStep = 1;
                const confirmed = await this.clientService.confirmAction(
                    this.l('ApproveRequest'),
                    this.l('ConfirmBrokerApproval')
                );

                if (isNil(confirmed)) {
                    break;
                }

                if (confirmed) {
                    this.clientsReferralService.createLastCertificateNumber()
                        .subscribe(response => {
                            this.updateExternalBrokerRequest(
                                this.referralId,
                                ExternalBrokerApprovalStatus.Approved,
                                response
                            );
                        });

                    this.sendApproveNotification(confirmed);

                    this.notify.success(this.l('ExternalBrokerApprovedSuccessfully', this.clientId, this.clientName));
                } else {
                    this.updateExternalBrokerRequest(
                        this.referralId,
                        ExternalBrokerApprovalStatus.Declined
                    );

                    this.sendApproveNotification(confirmed);

                    this.notify.info(this.l('ExternalBrokerDeclinedSuccessfully', this.clientId, this.clientName));
                }

                break;
            }
            default:
                break;
        }
    }

    addMenuItems(client: ClientDetailsResponseModel) {
        if (this.contactPermissionChecker.canPerformActionOnClient(client, [
            AppPermissions.AdministrationFullAccess, AppPermissions.ContactCreateSearch,
        ])) {
            this.menuItems.push({
                text: this.l('CreateSearch'),
                icon: 'search-house',
                onClick: () => this.handleActionClicked(ClientActionType.CreateSearch),
            });
        }

        if (this.contactPermissionChecker.canPerformActionOnClient(client, [
            AppPermissions.AdministrationFullAccess, AppPermissions.ContactCreateOffer,
        ])) {
            this.menuItems.push({
                text: this.l('CreateOffer'),
                icon: 'side-menu-listings',
                onClick: () => this.handleActionClicked(ClientActionType.CreateOffer),
            });
        }

        if (this.contactPermissionChecker.canPerformActionOnClient(client, [
            AppPermissions.AdministrationFullAccess, AppPermissions.ContactUploadDocument,
        ])) {
            this.menuItems.push({
                text: this.l('UploadNewDocument'),
                icon: 'contract',
                onClick: () => this.handleActionClicked(ClientActionType.AddNewDocument),
            });
        }

        if (this.contactPermissionChecker.canPerformActionOnClient(client, [
            AppPermissions.AdministrationFullAccess, AppPermissions.ContactUpdate,
        ])) {
            this.menuItems.push({
                text: this.l('Edit'),
                icon: 'edit-action',
                onClick: () => this.handleActionClicked(ClientActionType.Edit),
            });
        }

        if (this.clientTypeName === ClientTypeNames.Legal && this.contactPermissionChecker.canPerformActionOnClient(client, [
            AppPermissions.AdministrationFullAccess, AppPermissions.ContactCreateProject,
        ])) {
            const createOfferIndex = this.menuItems
                .findIndex(item => item.text === this.l('CreateOffer'));
            const newAction: IMenuItem = {
                text: this.l('CreateProject'),
                icon: 'side-menu-projects',
                onClick: () => this.handleActionClicked(ClientActionType.Project),
            };

            if (createOfferIndex !== -1) {
                this.menuItems.splice(createOfferIndex + 1, 0, newAction);
            } else {
                this.menuItems.push(newAction);
            }
        }

        // Physical contacts only - the merge takes two physical persons.
        if (this.clientTypeName !== ClientTypeNames.Legal && this.isGranted(AppPermissions.ContactsMerge)) {
            this.menuItems.push({
                text: this.l('ContactsMerge'),
                icon: 'users-group',
                onClick: () => this.handleActionClicked(ClientActionType.Merge),
            });
        }

        this.menuItems.push({
            text: this.l('ExternalBrokerRequest'),
            icon: 'add-broker',
            disabled: this.approvalStatus === ExternalBrokerApprovalStatus.Approved ||
                this.approvalStatus === ExternalBrokerApprovalStatus.Requested,
            onClick: () => this.handleActionClicked(ClientActionType.AddExternalBroker),
        });

        if (!isNil(this.approvalStatus) &&
            this.approvalStatus === ExternalBrokerApprovalStatus.Requested &&
        this.contactPermissionChecker.canApproveExternalBrokerOnClient(client, [AppPermissions.ContactExternalBrokerApprovalRight])) {
            this.menuItems.push({
                text: this.l('ExternalBrokerApprove'),
                icon: 'approve-broker',
                onClick: () => this.handleActionClicked(ClientActionType.ApproveExternalBroker),
            });
        }
    }

    showActions() {
        return this.menuItems.length > 0;
    }

    showEditActions() {
        return this.menuItems.length > 0 && this.menuItems.some(x => x.text === this.l('Edit'));
    }

    isLegalEntity(clientType: string) {
        return clientType === ClientTypeNames.Legal;
    }

    private createExternalBrokerRequest(onSuccess?: () => void) {
        if (isNil(this.clientId)) {
            return;
        }

        this.approvalStatus = ExternalBrokerApprovalStatus.Requested;

        this.clientsReferralService.create({
            clientId: this.clientId,
            approvalStatus: this.approvalStatus,
        } as ClientReferralRequestModel)
            .pipe(
                filter((id) => !isNil(id)),
                switchMap((id) => this.clientsReferralService.getDetails(id)),
            )
            .subscribe({
                next: (referral) => {
                    // Keep the referral id in sync so a follow-up approve/decline works
                    // without reloading the card.
                    this.referralId = referral.id;
                    this.menuItems = [];
                    this.addMenuItems(this.client);
                    onSuccess?.();
                },
                error: (err) => console.error('Create failed', err),
            });
    }

    private updateExternalBrokerRequest(
        referralId: number,
        status: ExternalBrokerApprovalStatus,
        certificate?: number | null,
        onSuccess?: () => void,
    ) {
        if (isNil(this.clientId)) {
            return;
        }

        this.approvalStatus = status;

        this.clientsReferralService.update(referralId, {
            clientId: this.clientId,
            approvalStatus: this.approvalStatus,
            certificate: certificate,
        } as ClientReferralRequestModel)
            .subscribe({
                next: (id) => {
                    if (!isNil(id)) {
                        this.menuItems = [];
                        this.addMenuItems(this.client);
                        onSuccess?.();
                    }
                },
                error: (err) => console.error('Update failed', err),
            });
    }

    private sendApproveNotification(isApproved: boolean) {
        // Referrals created before the tenant fix have no CreatorUserId, so there is nobody to
        // notify. Sending null fails model binding on ApproveExternalBrokerRequestModel.UserId.
        if (isNil(this.userId)) {
            return;
        }

        this.clientsReferralService.sendExternalBrokerApproveNotifications({
            clientId: this.clientId,
            clientName: this.clientName,
            isApproved: isApproved,
            userId: this.userId,
        } as ApproveExternalBrokerRequestModel)
            .subscribe(response => {});
    }
}

export default ClientCardComponent;
